import React, { useState, useEffect } from 'react';
import { fetchRatingHistory } from '../services/api';
import { useTranslation } from '../contexts/LanguageContext';
import '../css/RatingHistoryChart.css';

function RatingHistoryChart() {
    const { t } = useTranslation();
    const [history, setHistory] = useState([]);
    const [period, setPeriod] = useState('30d');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            try {
                const data = await fetchRatingHistory(period);
                if (!cancelled && data.success) {
                    setHistory([...data.history].reverse());
                }
            } catch (err) {
                console.error('Failed to load rating history:', err);
            }
            if (!cancelled) setLoading(false);
        }
        load();
        return () => { cancelled = true; };
    }, [period]);

    const renderChart = () => {
        if (history.length < 2) {
            return <p className="rh-no-data">{t('rank.history.noData')}</p>;
        }

        const width = 600, height = 200, pad = 40;
        const ratings = history.map(h => h.ratingAfter);
        const minR = Math.min(...ratings) - 50;
        const maxR = Math.max(...ratings) + 50;
        const rangeR = maxR - minR || 1;

        const x = (i) => pad + (i / (ratings.length - 1)) * (width - 2 * pad);
        const y = (r) => height - pad - ((r - minR) / rangeR) * (height - 2 * pad);

        const pathD = ratings.map((r, i) =>
            `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(r).toFixed(1)}`
        ).join(' ');

        return (
            <svg viewBox={`0 0 ${width} ${height}`} className="rh-svg">
                <line x1={pad} y1={y(1200)} x2={width - pad} y2={y(1200)}
                    stroke="var(--text-secondary)" strokeWidth="0.5" strokeDasharray="4 4" />
                <path d={pathD} fill="none" stroke="var(--focus-color, #6366f1)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                {ratings.map((r, i) => (
                    <circle key={i} cx={x(i)} cy={y(r)} r="3" fill="var(--focus-color, #6366f1)" />
                ))}
                <text x={pad - 5} y={y(maxR) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{Math.round(maxR)}</text>
                <text x={pad - 5} y={y(minR) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{Math.round(minR)}</text>
                {minR < 1200 && maxR > 1200 && (
                    <text x={pad - 5} y={y(1200) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">1200</text>
                )}
            </svg>
        );
    };

    return (
        <div className="rating-history-chart">
            <div className="rh-header">
                <h3 className="rh-title">{t('rank.history.title')}</h3>
                <div className="rh-period-selector">
                    {['30d', '90d', 'all'].map(p => (
                        <button
                            key={p}
                            className={`rh-period-btn ${period === p ? 'active' : ''}`}
                            onClick={() => setPeriod(p)}
                        >
                            {t(`rank.history.periods.${p}`)}
                        </button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div className="rh-loading">{t('common.loading')}</div>
            ) : (
                renderChart()
            )}
        </div>
    );
}

export default RatingHistoryChart;
